import { Box, Checkbox, FormControlLabel, Switch, Typography, Collapse } from '@mui/material';
import { ADMIN_SECTIONS, TABLET_ACCESS_KEY } from '../../config/adminPages';

interface Props {
  /** null = role default (everything the role shows) */
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  /** Staff: always restricted, no switch — they get only what is ticked (Jason, 31 Oct 2026). */
  alwaysRestricted?: boolean;
}

/**
 * Per-user page access as a tree of tick boxes — the sidebar's sections and
 * pages, from the same registry the sidebar and the route gate read. Ticking a
 * section stores the section key (every page in it, including ones added
 * later); ticking pages stores page keys. Off = role default.
 */
export default function PageAccessPicker({ value, onChange, alwaysRestricted = false }: Props) {
  const restricted = alwaysRestricted || value !== null;
  const has = (k: string) => (value ?? []).includes(k);

  const toggleSection = (sectionKey: string, pageKeys: string[]) => {
    const list = (value ?? []).filter((k) => k !== sectionKey && !pageKeys.includes(k));
    onChange(has(sectionKey) ? list : [...list, sectionKey]);
  };

  const togglePage = (sectionKey: string, pageKey: string, pageKeys: string[]) => {
    let list = value ?? [];
    if (has(sectionKey)) {
      // Expand the section into its pages minus this one.
      list = [...list.filter((k) => k !== sectionKey), ...pageKeys.filter((k) => k !== pageKey)];
    } else if (has(pageKey)) {
      list = list.filter((k) => k !== pageKey);
    } else {
      list = [...list, pageKey];
      // All pages ticked → collapse back to the section key.
      if (pageKeys.every((k) => list.includes(k))) list = [...list.filter((k) => !pageKeys.includes(k)), sectionKey];
    }
    onChange(list);
  };

  return (
    <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} data-testid="page-access">
      {alwaysRestricted ? (
        <>
          <Typography sx={{ fontWeight: 600 }}>Page access</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Staff see and use only what is ticked: the order tablet, and the sections and pages in their sidebar. Nothing ticked = no pages.
          </Typography>
        </>
      ) : (
        <>
          <FormControlLabel
            control={<Switch checked={restricted} onChange={(e) => onChange(e.target.checked ? [] : null)} />}
            label={<Typography sx={{ fontWeight: 600 }}>Restrict to specific pages</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mb: 1 }}>
            {restricted
              ? 'Only what is ticked: the order tablet, and the sections and pages in their sidebar.'
              : 'Off: the user sees everything their role allows.'}
          </Typography>
        </>
      )}
      <Collapse in={restricted}>
        {/* Not an admin page: the order tablet. Enforced by the API, unlike the page ticks. */}
        <Box sx={{ ml: 1, mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={has(TABLET_ACCESS_KEY)}
                onChange={() => onChange(has(TABLET_ACCESS_KEY)
                  ? (value ?? []).filter((k) => k !== TABLET_ACCESS_KEY)
                  : [...(value ?? []), TABLET_ACCESS_KEY])}
                inputProps={{ 'aria-label': 'access Order tablet' }}
              />
            }
            label={<Typography sx={{ fontWeight: 600, fontSize: 13 }}>Order tablet</Typography>}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
            Sign in to the order manager for their assigned stores. Tick only this for a tablet-only account.
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, ml: 1 }}>
          {ADMIN_SECTIONS.map((section) => {
            const pageKeys = section.pages.map((p) => p.key);
            const all = has(section.key);
            const some = !all && pageKeys.some(has);
            return (
              <Box key={section.key} sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={all}
                      indeterminate={some}
                      onChange={() => toggleSection(section.key, pageKeys)}
                      inputProps={{ 'aria-label': `section ${section.label}` }}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 600, fontSize: 13 }}>{section.label}</Typography>}
                />
                <Box sx={{ ml: 3, display: 'flex', flexDirection: 'column' }}>
                  {section.pages.map((page) => (
                    <FormControlLabel
                      key={page.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={all || has(page.key)}
                          onChange={() => togglePage(section.key, page.key, pageKeys)}
                          inputProps={{ 'aria-label': `page ${page.label}` }}
                        />
                      }
                      label={<Typography sx={{ fontSize: 13 }}>{page.label}</Typography>}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}
